export const CHECKOUT_URL = 'https://refinement.impultienda.ar/checkout'

export const HERO_IMAGE =
  'https://cdn.magicpatterns.com/uploads/2RM9LyZxaRkYCFxhbAzKDi/REFINAMIENTO-EN.png'

export const KARITO_IMAGE =
  'https://cdn.magicpatterns.com/patterns/generated-images/dcfd6c16-19e7-446e-a0b6-832935b40bb0.jpg'

export const stats = [
  { value: '6', label: 'Clinical Volumes' },
  { value: '300+', label: 'Protocols & Checklists' },
  { value: '5', label: 'Free Clinical Tools' },
  { value: '4.9/5', label: 'Average Rating' },
]

export const withoutSystem = [
  'Refinements that extend treatment longer than expected.',
  'Approving digital setups simply because they look good on screen.',
  'Relying on default attachment designs without considering the actual movement.',
  'Discovering tracking problems only after the patient returns.',
  'Spending additional appointments correcting problems that could have been anticipated.',
  'Patients repeatedly asking: “Doctor, how much longer will this take?”',
]

export const withSystem = [
  'A more structured approach to aligner planning.',
  'Clinical checklists before approving a treatment plan.',
  'Better understanding of predictable vs. challenging movements.',
  'Protocols for attachments, IPR and mechanical control.',
  'Early detection of tracking problems.',
  'Practical rescue strategies before immediately requesting a refinement.',
]

export const painPoints = [
  {
    icon: '😓',
    title: 'The case looked perfect on the screen…',
    body: 'You approved the plan because the digital setup looked great. Then the patient comes back and one tooth simply isn’t where it should be. Now you’re wondering: “Where did this go wrong?”',
  },
  {
    icon: '⏱️',
    title: 'Your schedule keeps filling up with extra appointments',
    body: 'The case was supposed to take a certain amount of time. Instead you’re seeing the patient again and again. More adjustments, more monitoring, more chair time — and less room for new cases.',
  },
  {
    icon: '🤔',
    title: 'You trust the software — but clinical reality is different',
    body: 'The software says the movement is possible. The simulation looks perfect. But a digital setup doesn’t tell you how predictably that movement will express clinically. You need to know what to look for before clicking “Approve.”',
  },
  {
    icon: '💸',
    title: 'The case isn’t as profitable as you expected',
    body: 'You calculated the fee based on a predictable workflow. But additional appointments, corrections and refinements slowly eat away at your margin. The patient may never see the difference. Your schedule and your bottom line do.',
  },
  {
    icon: '😩',
    title: 'The patient starts asking why treatment is taking so long',
    body: 'Every additional appointment creates another opportunity for frustration. Eventually you hear: “I thought I would be finished by now.” The problem isn’t always compliance — sometimes it began during planning.',
  },
  {
    icon: '🧠',
    title: 'You were taught how to move teeth…',
    body: 'But nobody gave you a practical system for identifying which movements are more likely to fail, where tracking problems begin, and what to do when a case starts moving away from the original plan.',
  },
]

export const systemFeatures = [
  {
    icon: '📘',
    title: '6 Clinical Volumes',
    body: 'A complete system covering the journey from initial diagnosis and planning to tracking, rescue protocols, finishing and retention.',
  },
  {
    icon: '🦷',
    title: '300+ Protocols & Checklists',
    body: 'Practical tools covering treatment planning, attachments, IPR, movement control, tracking and clinical decision-making.',
  },
  {
    icon: '🎯',
    title: 'Predictability Frameworks',
    body: 'Identify higher-risk movements before approving a treatment plan and anticipate potential problems earlier.',
  },
  {
    icon: '⚡',
    title: 'Clinical Rescue Protocols',
    body: 'Practical strategies for when a tooth begins to fall behind, instead of immediately jumping to another refinement.',
  },
  {
    icon: '🧭',
    title: 'Clinical Decision Trees',
    body: 'Visual decision-making tools that help you determine what to check, what to adjust and when to escalate.',
  },
  {
    icon: '📊',
    title: 'Case Management Tools',
    body: 'Checklists, quick-reference guides and practical workflows designed to make your aligner cases easier to manage.',
  },
]

export const volumes = [
  {
    number: 'Volume 1',
    title: 'The Anatomy of an Aligner Refinement',
    lead: 'Learn why aligner cases go off track — and how to recognize high-risk situations before treatment begins.',
    body: 'Understand the difference between necessary and preventable refinements, identify predictable vs. challenging movements, recognize common risk factors and use an initial case-screening checklist before starting treatment.',
  },
  {
    number: 'Volume 2',
    title: 'Anti-Refinement Treatment Planning',
    lead: 'Stop looking at digital setups only for how good they look. Review them with clinical judgment.',
    body: 'A practical framework for evaluating movement sequencing, torque, rotation, vertical control, expansion, distalization, anchorage and strategic overcorrection before approving the plan.',
  },
  {
    number: 'Volume 3',
    title: 'Attachments, IPR & Mechanical Control',
    lead: 'Master the three elements that determine whether planned movements are actually expressed.',
    body: 'Approach attachments based on the movement required, understand IPR from a clinical perspective, control contacts and improve mechanical control for challenging movements.',
  },
  {
    number: 'Volume 4',
    title: 'Clinical Execution & Tracking',
    lead: 'Catch problems early — before they turn into full-blown refinements.',
    body: 'A practical workflow for delivery appointments, patient instructions, tracking assessment, attachment checks, recognizing early warning signs and deciding when to continue, pause or intervene.',
  },
  {
    number: 'Volume 5',
    title: 'Aligner Rescue Biomechanics',
    lead: 'Know what to do when the case starts moving away from the plan.',
    body: 'Evaluate a tooth that stops tracking, know when to consider auxiliaries, buttons or elastics, when to step back, when to pause and when a refinement is actually justified.',
  },
  {
    number: 'Volume 6',
    title: 'Finishing, Retention & Profitability',
    lead: 'Finish cases with clinical judgment instead of extending treatment indefinitely.',
    body: 'Define a clinically acceptable finish, manage final adjustments, communicate case completion, transition into retention and understand the real financial impact of unnecessary refinements.',
  },
]

export const bonuses = [
  {
    tag: 'Bonus #1',
    title: 'Refinement Cost & Profitability Calculator™',
    lead: 'See what every unnecessary refinement is really costing your practice.',
    body: 'Calculate the hidden cost of additional chair time, extra appointments, materials, laboratory expenses and lost opportunities.',
    value: '$37',
    image:
      'https://cdn.magicpatterns.com/uploads/jgVBB7GdJPTqhhy9pjXMbS/BONUS_1_REFINAMIENTO-EN.png',
  },
  {
    tag: 'Bonus #2',
    title: 'Express Clinical Correction Guide™',
    lead: 'Know what to check before immediately requesting a refinement.',
    body: 'A quick-reference clinical guide with practical correction strategies for common tracking and movement problems.',
    value: '$47',
    image:
      'https://cdn.magicpatterns.com/uploads/ueoEMoG8FuDGvY2LZcgqTn/BONUS_2_REFINAMIENTO-EN.png',
  },
  {
    tag: 'Bonus #3',
    title: 'Patient Communication Scripts™',
    lead: 'Set expectations clearly. Improve compliance. Reduce frustration.',
    body: 'Ready-to-use scripts for consultations, follow-ups and WhatsApp communication about timelines, wear, tracking and refinements.',
    value: '$27',
    image:
      'https://cdn.magicpatterns.com/uploads/5xmwM78FiYoeEVnyTfFf4c/BONUS_3_REFINAMIENTO-EN.png',
  },
  {
    tag: 'Bonus #4',
    title: 'ClinCheck Approval Master Checklist™',
    lead: 'Before you click “Approve,” check the things that matter.',
    body: 'A structured clinical checklist to review key treatment-planning factors before approving a digital setup.',
    value: '$37',
    image:
      'https://cdn.magicpatterns.com/uploads/kTi7dU3yooBQGNFkVx2saP/BONUS_4_REFINAMIENTO-EN.png',
  },
  {
    tag: 'Bonus #5',
    title: 'Express Case Finishing Protocol™',
    lead: 'Don’t let a nearly finished case turn into another refinement.',
    body: 'A practical finishing protocol to evaluate the final stage of treatment, improve settling and decide if the case is ready to finish.',
    value: '$37',
    image:
      'https://cdn.magicpatterns.com/uploads/sg9rHAfLF6trdiSVUqxvat/BONUS_5_REFINAMIENTO-EN.png',
  },
]

export const testimonials = [
  {
    name: 'Dr. M.P.',
    country: 'United States 🇺🇸',
    quote:
      'I used to approve digital setups mainly based on how good they looked. The planning checklist gave me a much more structured way to review cases before treatment starts.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/43645c78-7a2e-4e4c-94cb-fa5c68148cf7.jpg',
  },
  {
    name: 'Dr. R.G.',
    country: 'United Kingdom 🇬🇧',
    quote:
      'The attachment and movement-control sections were extremely practical. I now have a much clearer framework for identifying movements that may be difficult to express.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/0d618873-9990-4343-a01c-7746c7de6b63.jpg',
  },
  {
    name: 'Dr. A.L.',
    country: 'Australia 🇦🇺',
    quote:
      'The rescue protocols are probably my favorite part. Instead of immediately thinking about a refinement when tracking deteriorates, I now have a process to evaluate the situation first.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/cd8b5f62-1278-4951-95c0-e578976314b1.jpg',
  },
  {
    name: 'Dr. C.S.',
    country: 'Canada 🇨🇦',
    quote:
      'The patient scripts are surprisingly useful. They make it much easier to explain treatment timelines and set expectations before problems arise.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/3b30f74e-0746-4895-ab58-7c49741e0cd1.jpg',
  },
  {
    name: 'Dr. J.M.',
    country: 'Ireland 🇮🇪',
    quote:
      'It is practical rather than theoretical. I can quickly find the section I need and use it as a reference during my aligner workflow.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/4f5c4c19-d449-478a-90f7-5e215b27f60c.jpg',
  },
]

export const faqs = [
  {
    q: 'How will I receive the system?',
    a: 'Immediately after your purchase you’ll receive an email with access to download the 6 clinical volumes and all 5 bonuses.',
  },
  {
    q: 'Is this compatible with different aligner systems?',
    a: 'Yes. The system focuses on clinical principles, treatment planning, biomechanics, tracking and decision-making that can be applied across different clear-aligner systems.',
  },
  {
    q: 'Do I need any special software?',
    a: 'No. The materials are delivered digitally and can be accessed from your computer, tablet or smartphone.',
  },
  {
    q: 'Is this a subscription?',
    a: 'No. It’s a one-time payment of $27 USD. There are no monthly fees or recurring charges.',
  },
  {
    q: 'How long do I have access?',
    a: 'Lifetime access. Once you purchase the system you can return to the materials whenever you need them.',
  },
  {
    q: 'Is this only for orthodontists?',
    a: 'No. It’s designed for dental professionals who plan and treat cases with clear aligners, including orthodontists and general dentists working with aligner therapy.',
  },
  {
    q: 'Is this a course?',
    a: 'No. The Anti-Refinement System™ is a practical clinical reference system, so you can quickly find the protocol you need instead of sitting through hours of video training.',
  },
]

export const buyerNotifications = [
  { name: 'Dra. Valentina R.', country: 'Argentina 🇦🇷' },
  { name: 'Dr. Andrés M.', country: 'Colombia 🇨🇴' },
  { name: 'Dra. Camila S.', country: 'Chile 🇨🇱' },
  { name: 'Dr. Javier L.', country: 'España 🇪🇸' },
  { name: 'Dra. Fernanda G.', country: 'México 🇲🇽' },
  { name: 'Dr. Sebastián P.', country: 'Perú 🇵🇪' },
  { name: 'Dra. Mariana T.', country: 'Uruguay 🇺🇾' },
  { name: 'Dr. Ricardo H.', country: 'Ecuador 🇪🇨' },
  { name: 'Dra. Lucía B.', country: 'Costa Rica 🇨🇷' },
  { name: 'Dr. Gabriel N.', country: 'República Dominicana 🇩🇴' },
  { name: 'Dra. Paula V.', country: 'Panamá 🇵🇦' },
  { name: 'Dr. Emilio C.', country: 'Guatemala 🇬🇹' },
]
